import {
  Button,
  Dialog,
  FormGroup,
  Input,
  Select,
  Text,
} from '@southfields-digital/mpxlive-components';
import type { ChangeEvent } from 'react';

import type { AutoUpdateConfig } from '../../application/autoUpdateManager';

import styles from './DataConnectorPanel.module.scss';

type AutoUpdateModalProps = {
  isOpen: boolean;
  values: AutoUpdateConfig;
  onChange: (values: AutoUpdateConfig) => void;
  onClose: () => void;
  onSave: () => void;
  onDisable: () => void;
  canDisable: boolean;
};

export const AutoUpdateModal = ({
  isOpen,
  values,
  onChange,
  onClose,
  onSave,
  onDisable,
  canDisable,
}: AutoUpdateModalProps) => {
  const handleIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextInterval = Number(event.currentTarget.value);
    onChange({
      ...values,
      interval: Number.isNaN(nextInterval) ? values.interval : nextInterval,
    });
  };

  const handlePushChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...values,
      pushWhenOnAir: event.currentTarget.value === 'yes',
    });
  };

  return (
    <Dialog backdrop open={isOpen} onClose={onClose}>
      <Dialog.Panel className={styles.AutoUpdateDialog}>
        <Dialog.Title>Configure auto-update</Dialog.Title>

        <div className={styles.AutoUpdateDialogContent}>
          <FormGroup label="Interval (seconds)" htmlFor="auto-update-interval">
            <Input
              id="auto-update-interval"
              type="number"
              min={1}
              value={values.interval}
              onChange={handleIntervalChange}
            />
          </FormGroup>

          <FormGroup label="Push when on-air" htmlFor="auto-update-push">
            <Select
              id="auto-update-push"
              value={values.pushWhenOnAir ? 'yes' : 'no'}
              onChange={handlePushChange}
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </FormGroup>

          <Text as="p" className={styles.AutoUpdateDialogNote}>
            Auto-update keeps data in sync by subscribing to the connector. Runtime datafields are
            locked while it is enabled.
          </Text>
        </div>

        <Dialog.Footer className={styles.AutoUpdateDialogFooter}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {canDisable && (
            <Button variant="danger" onClick={onDisable}>
              Disable
            </Button>
          )}
          <Button variant="primary" onClick={onSave}>
            {canDisable ? 'Update' : 'Enable'}
          </Button>
        </Dialog.Footer>
      </Dialog.Panel>
    </Dialog>
  );
};
